import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Optional, SkipSelf, Type } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { AppRoute, BypassSecurityPipe, ByteToMegaBytePipe, goToDashboardIfLoggedIn } from '@ebeihilfe/core';
import { ConverterService, EndToEndEncrypter, PendingChangesGuard, SimpleProgressComponent } from '@ebeihilfe/core';
import {
  EINREICHUNG_COMPONENT,
  EinreichungComponentType,
  EinreichungService,
  OldCoreEinreichungModule,
  SeiteFactory,
} from '@ebeihilfe/einreichung-old';
import { EinreichungApiService } from '@ebeihilfe/einreichung-shared/services';
import { DynamicFormComponent } from '@ebeihilfe/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { CustomComponentModule } from '../../components/component.module';
import { StartPageComponent } from '../pages/start-page/start-page.component';
import { AntragModalComponent } from './antrag-modal/antrag-modal.component';
import { BelegSelectionComponent } from './beleg-selection/beleg-selection.component';
import { ConfirmationStepComponent } from './confirmation-step/confirmation-step.component';
import { PopulateUnfinishedAntragComponent } from './populate-unfinished-antrag/populate-unfinished-antrag.component';
import { PortalEinreichungComponent } from './portal-einreichung.component';
import { StartupInformationComponent } from './startup-information/startup-information.component';
import { StepperComponent } from './stepper/stepper.component';
import { UploadComponent } from './upload/upload.component';
import { WebSeiteFactory } from './WebSeiteFactory';

@NgModule({
  declarations: [UploadComponent, ConfirmationStepComponent, StartupInformationComponent],
  exports: [ConfirmationStepComponent],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AngularSvgIconModule,
    CustomComponentModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatProgressBarModule,
    SimpleProgressComponent,
    MatCardModule,
    OldCoreEinreichungModule,
    RouterModule.forChild([
      {
        component: PortalEinreichungComponent,
        path: AppRoute.EinreichungOld,
        canDeactivate: [PendingChangesGuard],
        data: { title: 'page_names.startseite' },
      },
      {
        path: '',
        pathMatch: 'full',
        component: StartPageComponent,
        canActivate: [goToDashboardIfLoggedIn],
        data: { title: 'page_names.startseite' },
      },
    ]),
    MatNativeDateModule,
    MatDividerModule,
    MatRadioModule,
    A11yModule,
    MatSelectModule,
    PopulateUnfinishedAntragComponent,
    AntragModalComponent,
    DynamicFormComponent,
    BypassSecurityPipe,
    ByteToMegaBytePipe,
    StepperComponent,
    PortalEinreichungComponent,
  ],
  providers: [
    EinreichungService,
    {
      provide: SeiteFactory,
      useFactory: (): WebSeiteFactory => new WebSeiteFactory(),
      deps: [EinreichungApiService, EndToEndEncrypter, ConverterService],
    },
  ],
})
export class EinreichungOldModule {
  constructor(@Optional() @SkipSelf() parentModule: EinreichungOldModule) {
    if (parentModule) {
      // todo do we need to prevent this from being imported multiple times?
    }
  }

  public static forRoot(steps: Type<EinreichungComponentType>[] = []): ModuleWithProviders<EinreichungOldModule> {
    const providers = steps.map((step: Type<EinreichungComponentType>) => ({
      provide: EINREICHUNG_COMPONENT,
      multi: true,
      useClass: step,
    }));

    return {
      ngModule: EinreichungOldModule,
      providers: [
        {
          provide: EINREICHUNG_COMPONENT,
          multi: true,
          useClass: PopulateUnfinishedAntragComponent,
        },
        {
          provide: EINREICHUNG_COMPONENT,
          multi: true,
          useClass: StartupInformationComponent,
        },
        {
          provide: EINREICHUNG_COMPONENT,
          multi: true,
          useClass: BelegSelectionComponent,
        },
        {
          provide: EINREICHUNG_COMPONENT,
          multi: true,
          useClass: UploadComponent,
        },
        ...providers,
      ],
    };
  }
}
